import { useQuery } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'
import { mensajeDeError } from '@/lib/supabase/errores'

/** Una factura de venta de World Office, con la orden a la que quedó enlazada. */
export type FacturaDeVenta = {
  id_asiento: number
  documento: string
  fecha: string | null
  cliente_nombre: string | null
  cliente_identificacion: string | null
  vendedor_nombre: string | null
  total: number | null
  electronica: boolean | null
  cufe: string | null
  anulado: boolean
  synced_at: string | null
  renglones: number
  orden_servicio_id: string | null
  orden_numero: string | null
}

export type RenglonDeFactura = {
  renglon: number
  producto: string | null
  cantidad: number | null
  unidad: string | null
  precio_unitario: number | null
  total_renglon: number | null
}

export type FiltrosFacturas = {
  busqueda: string
  dias: number
  soloSinEnlazar: boolean
}

export const FILTROS_INICIALES: FiltrosFacturas = { busqueda: '', dias: 30, soloSinEnlazar: false }

const POR_PAGINA = 50

export function useFacturasDeVenta(filtros: FiltrosFacturas, pagina: number) {
  const consulta = useQuery({
    queryKey: ['facturas-de-venta', filtros, pagina],
    queryFn: async () => {
      let q = supabase
        .from('vista_facturas_de_venta' as never)
        .select('*', { count: 'exact' })
        .order('fecha', { ascending: false })
        .order('id_asiento', { ascending: false })
        .range(pagina * POR_PAGINA, pagina * POR_PAGINA + POR_PAGINA - 1)

      const texto = filtros.busqueda.trim().replace(/[%,()]/g, ' ')
      if (texto) {
        // Número, cliente o NIT: es lo que la gente tiene en la mano.
        q = q.or(
          `documento.ilike.%${texto}%,cliente_nombre.ilike.%${texto}%,cliente_identificacion.ilike.%${texto}%`,
        )
      }
      if (filtros.dias > 0) {
        const desde = new Date(Date.now() - filtros.dias * 86_400_000).toISOString().slice(0, 10)
        q = q.gte('fecha', desde)
      }
      if (filtros.soloSinEnlazar) q = q.is('orden_servicio_id', null)

      const { data, error, count } = await q
      if (error) throw new Error(mensajeDeError(error))

      return { filas: (data ?? []) as unknown as FacturaDeVenta[], total: count ?? 0 }
    },
  })

  return {
    facturas: consulta.data?.filas ?? [],
    total: consulta.data?.total ?? 0,
    porPagina: POR_PAGINA,
    cargando: consulta.isLoading,
    error: consulta.error?.message ?? null,
  }
}

/**
 * Cuántas hay y cuándo llegó la última.
 *
 * La fecha de sincronización es lo que responde la pregunta de verdad cuando
 * la lista sale vacía: si no hay facturas, o si el worker de World Office
 * todavía no las ha traído.
 */
export function useResumenFacturas() {
  return useQuery({
    queryKey: ['facturas-de-venta', 'resumen'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vista_facturas_de_venta' as never)
        .select('id_asiento, orden_servicio_id, synced_at')
      if (error) throw new Error(mensajeDeError(error))

      const filas = (data ?? []) as unknown as Pick<
        FacturaDeVenta,
        'id_asiento' | 'orden_servicio_id' | 'synced_at'
      >[]
      const ultima = filas.reduce<string | null>(
        (m, f) => (f.synced_at && (!m || f.synced_at > m) ? f.synced_at : m),
        null,
      )
      return {
        total: filas.length,
        enlazadas: filas.filter((f) => f.orden_servicio_id).length,
        ultima,
      }
    },
  }).data
}

export function useRenglonesDeFactura(idAsiento: number | null) {
  return useQuery({
    queryKey: ['facturas-de-venta', 'renglones', idAsiento],
    enabled: idAsiento !== null,
    queryFn: async (): Promise<RenglonDeFactura[]> => {
      const { data, error } = await supabase
        .from('wo_factura_renglones' as never)
        .select('renglon, producto, cantidad, unidad, precio_unitario, total_renglon')
        .eq('id_asiento', idAsiento!)
        .order('renglon')
      if (error) throw new Error(mensajeDeError(error))
      return (data ?? []) as unknown as RenglonDeFactura[]
    },
  })
}
